import { useEffect } from "react";

/**
 * One-shot reveal-on-scroll: attaches `.is-visible` to every
 * `[data-reveal]` element when ~15% of it enters the viewport. Extracted
 * from Landing.jsx so the page component stays focused on layout.
 */
export function useScrollReveal() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const els = document.querySelectorAll("[data-reveal]");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/**
 * Drives the top scroll-progress hairline (`#kpp-scroll-bar`) and toggles
 * the mobile sticky CTA (`#kpp-sticky-cta`) past 60% of the viewport
 * scroll. Extracted from Landing.jsx — both elements are rendered as
 * portals in the static markup so the hook can stay decoupled.
 */
export function useScrollProgressBar() {
  useEffect(() => {
    const bar = document.getElementById("kpp-scroll-bar");
    const cta = document.getElementById("kpp-sticky-cta");
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const pct = max > 0 ? h.scrollTop / max : 0;
      if (bar) bar.style.transform = `scaleX(${pct})`;
      if (cta) {
        if (h.scrollTop > h.clientHeight * 0.6) cta.classList.add("is-visible");
        else cta.classList.remove("is-visible");
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
}
